import React, { useState, useEffect } from 'react';
import { qs } from 'utils';

import {
  defaultQueryParams,
  parseQueryParams,
  updateBrowserUrl,
} from 'src/utils/parseQueryParams';
import { getWindowLocation } from 'src/utils/window';

import { QueryParamContext, type QueryParamTab } from '../useQueryParam';

export interface QueryParams {
  page: number;
  tab: QueryParamTab;
  q?: string;
  enabledFeatures: string[];
}

interface NewQueryParams {
  page: number;
  tab?: QueryParamTab;
  q?: string;
}

const updateQueryParams = (
  queryParams: QueryParams,
  newParams: NewQueryParams,
) => {
  const params = qs.parse(getWindowLocation().search);

  const updatedParams = {
    ...queryParams,
    ...newParams,
  };

  updateBrowserUrl(params, updatedParams, false);
};

export const QueryParamProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [queryParams, setQueryParams] =
    useState<QueryParams>(defaultQueryParams);

  const handlePopstate = React.useCallback(() => {
    setQueryParams(parseQueryParams(getWindowLocation().search));
  }, []);

  useEffect(() => {
    const param = parseQueryParams(getWindowLocation().search);
    setQueryParams(param);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [handlePopstate]);

  const updateQueryParamsAndUrl = (newParams: NewQueryParams) => {
    setQueryParams((prevState) => ({ ...prevState, ...newParams }));
    updateQueryParams(queryParams, newParams);
  };

  const setPage = (page: number) => {
    updateQueryParamsAndUrl({ page });
  };

  const setTab = (tab: QueryParamTab) => {
    updateQueryParamsAndUrl({ tab, page: 1 });
  };

  const setQ = (q: string) => {
    updateQueryParamsAndUrl({ q, page: 1 });
  };

  return (
    <QueryParamContext.Provider
      value={{ ...queryParams, setPage, setTab, setQ }}
    >
      {children}
    </QueryParamContext.Provider>
  );
};
